const liters = new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})
const money = new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const dateTime = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit', month: '2-digit', year: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
})
const timeOnly = new Intl.DateTimeFormat('uk-UA', {
  hour: '2-digit', minute: '2-digit',
})
const dateAndTime = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
})

const typeOrder = { start: 0, sale: 1, delivery: 2, end: 3 }

export function orderedTankEvents(events = []) {
  return events
    .map((event, index) => ({ event, index, timestamp: Date.parse(event.at) }))
    .filter(({ event, timestamp }) => Number.isFinite(timestamp) && Number.isFinite(event.liters))
    .sort((left, right) => left.timestamp - right.timestamp
      || (typeOrder[left.event.type] ?? 9) - (typeOrder[right.event.type] ?? 9)
      || left.index - right.index)
    .map(({ event }) => event)
}

export function chartYAxisMax(series) {
  const capacity = series?.tank?.capacityLiters
  if (Number.isFinite(capacity) && capacity > 0) return capacity
  const values = (series?.events ?? []).flatMap((event) => [event.liters, event.beforeLiters])
    .filter((value) => Number.isFinite(value) && value >= 0)
  const observed = Math.max(0, ...values)
  const step = observed >= 10_000 ? 1_000 : observed >= 2_000 ? 500 : 100
  return Math.max(step, Math.ceil((observed * 1.08) / step) * step)
}

export function shiftDurationLabel(interval) {
  const start = Date.parse(interval?.startedAt)
  const end = Date.parse(interval?.endedAt)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 'Тривалість невідома'
  const minutes = Math.floor((end - start) / 60_000)
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return `${hours} год ${remainder} хв`
}

export function formatShiftInterval(interval) {
  if (!interval?.startedAt || !interval?.endedAt) return 'Період зміни не визначено'
  return `${dateTime.format(new Date(interval.startedAt))} — ${dateTime.format(new Date(interval.endedAt))} · ${shiftDurationLabel(interval)}`
}

export function deliveryTooltipHtml(event, tank = {}) {
  const capacity = Number.isFinite(tank.capacityLiters)
    ? `${liters.format(tank.capacityLiters)} л` : 'не налаштовано'
  const fill = Number.isFinite(event.fillPercent)
    ? `${liters.format(event.fillPercent)}%` : 'не визначено'
  const derivedNote = event.levelBasis === 'derived_previous_plus_delivery'
    ? '<small class="delivery-tooltip-note">Рівні розраховано за попередньою фактичною точкою та обсягом надходження.</small>'
    : ''
  return `<div class="tank-chart-tooltip tank-chart-tooltip-delivery">
    <strong><span class="delivery-tooltip-badge">Надходження</span> ${escapeHtml(dateTime.format(new Date(event.at)))}</strong>
    <span>${escapeHtml(tankLabel(tank))}</span>
    <dl><div><dt>До надходження</dt><dd>${formatLiters(event.beforeLiters)}</dd></div>
    <div><dt>Надходження</dt><dd>+${formatLiters(event.deliveredLiters)}</dd></div>
    <div><dt>Після надходження</dt><dd>${formatLiters(event.liters)}</dd></div>
    <div><dt>Місткість</dt><dd>${escapeHtml(capacity)}</dd></div>
    <div><dt>Заповнення</dt><dd>${escapeHtml(fill)}</dd></div></dl>${derivedNote}
  </div>`
}

export function saleTooltipHtml(event, tank = {}) {
  return `<div class="tank-chart-tooltip tank-chart-tooltip-sale">
    <strong>Реалізація</strong>
    <span>${escapeHtml(dateTime.format(new Date(event.at)))}</span>
    <small>${escapeHtml(tankLabel(tank))}</small>
    <dl><div><dt>Літраж</dt><dd>${formatLiters(event.soldLiters)}</dd></div>
    <div><dt>Ціна</dt><dd>${formatMoney(event.unitPrice, 'UAH/л')}</dd></div>
    <div><dt>Сума</dt><dd>${formatMoney(event.paymentAmount, 'UAH')}</dd></div>
    <div><dt>Залишок</dt><dd>${formatLiters(event.liters)}</dd></div></dl>
  </div>`
}

export function buildHistoricalTankChartOptions(series, zoom = { start: 0, end: 100 }) {
  const events = orderedTankEvents(series?.events)
  const intervalStart = Date.parse(series?.interval?.startedAt)
  const intervalEnd = Date.parse(series?.interval?.endedAt)
  const durationHours = Number.isFinite(intervalStart) && Number.isFinite(intervalEnd)
    ? Math.max(0, (intervalEnd - intervalStart) / 3_600_000) : 0
  const primaryData = []
  const deliveries = []

  for (const event of events) {
    const timestamp = Date.parse(event.at)
    if (event.type === 'delivery' && Number.isFinite(event.beforeLiters)) {
      primaryData.push({
        value: [timestamp, event.beforeLiters], event,
        pointKind: 'delivery-before', symbolSize: 0, tooltip: { show: false },
      })
      deliveries.push(event)
    }
    primaryData.push({ value: [timestamp, event.liters], event, pointKind: event.type })
  }

  const deliverySegments = deliveries.map((event, index) => ({
    id: `delivery-segment-${index}`,
    name: 'Надходження',
    type: 'line',
    data: [
      [Date.parse(event.at), event.beforeLiters],
      [Date.parse(event.at), event.liters],
    ],
    smooth: false,
    showSymbol: false,
    silent: true,
    animation: false,
    lineStyle: { color: '#23a55a', width: 4 },
    z: 7,
  }))

  const deliveryPoints = deliveries.map((event) => ({
    value: [Date.parse(event.at), event.liters], event,
  }))
  const yMax = chartYAxisMax(series)
  const hasCapacity = Number.isFinite(series?.tank?.capacityLiters)

  return {
    animationDuration: 320,
    aria: {
      enabled: true,
      decal: { show: true },
      description: `Історія залишку ${tankLabel(series?.tank)}. Зелені ромби позначають надходження пального.`,
    },
    grid: { left: 72, right: 30, top: 40, bottom: 116, containLabel: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        snap: true,
        lineStyle: { color: '#7dabad', width: 1, type: 'dashed' },
      },
      confine: true,
      appendToBody: false,
      backgroundColor: '#ffffff',
      borderColor: '#b8c9cc',
      borderWidth: 1,
      padding: 0,
      extraCssText: 'border-radius:10px;box-shadow:0 12px 30px rgba(18,50,58,.18);',
      formatter(params) {
        const items = Array.isArray(params) ? params : [params]
        const deliveryItem = items.find((item) => item?.data?.event?.type === 'delivery')
        const levelItem = items.find((item) => item?.seriesId === 'tank-level' && item?.data?.event)
        const event = (deliveryItem ?? levelItem ?? items.find((item) => item?.data?.event))?.data?.event
        if (!event) return ''
        if (event.type === 'delivery') return deliveryTooltipHtml(event, series?.tank)
        if (event.type === 'sale') return saleTooltipHtml(event, series?.tank)
        const title = event.type === 'start' ? 'На початок зміни'
          : 'На кінець зміни'
        return `<div class="tank-chart-tooltip"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(dateTime.format(new Date(event.at)))}</span><b>${formatLiters(event.liters)}</b><small>${escapeHtml(tankLabel(series?.tank))}</small></div>`
      },
    },
    xAxis: {
      type: 'time',
      min: Number.isFinite(intervalStart) ? intervalStart : undefined,
      max: Number.isFinite(intervalEnd) ? intervalEnd : undefined,
      boundaryGap: false,
      splitNumber: durationHours <= 8 ? 5 : durationHours <= 36 ? 7 : 9,
      axisPointer: {
        show: true,
        snap: true,
        label: { show: false },
      },
      axisLine: { lineStyle: { color: '#a8bcc0' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#637b82',
        hideOverlap: true,
        margin: 16,
        formatter(value) {
          return durationHours > 24 ? dateAndTime.format(new Date(value)) : timeOnly.format(new Date(value))
        },
      },
      splitLine: { show: true, lineStyle: { color: '#e8eeee', type: 'dashed' } },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: yMax,
      name: hasCapacity ? 'Обʼєм, л' : 'Обʼєм, л · автошкала',
      nameLocation: 'end',
      nameGap: 18,
      nameTextStyle: { color: '#526d75', fontWeight: 650, align: 'left' },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#637b82', formatter: (value) => liters.format(value) },
      splitLine: { lineStyle: { color: '#e4ecee', type: 'dashed' } },
    },
    dataZoom: [
      {
        id: 'tank-history-slider',
        type: 'slider',
        start: zoom.start,
        end: zoom.end,
        bottom: 20,
        height: 56,
        filterMode: 'none',
        showDataShadow: true,
        brushSelect: true,
        borderColor: '#d4e0e2',
        backgroundColor: '#f4f8f8',
        fillerColor: 'rgba(8, 103, 114, .13)',
        dataBackground: {
          lineStyle: { color: '#7dabad', width: 1 },
          areaStyle: { color: 'rgba(37, 143, 148, .12)' },
        },
        selectedDataBackground: {
          lineStyle: { color: '#087781', width: 1.5 },
          areaStyle: { color: 'rgba(8, 119, 129, .18)' },
        },
        handleStyle: { color: '#ffffff', borderColor: '#087781', borderWidth: 2 },
        moveHandleStyle: { color: '#087781', opacity: 0.35 },
        textStyle: { color: '#5e747a' },
      },
      { id: 'tank-history-inside', type: 'inside', start: zoom.start, end: zoom.end, filterMode: 'none' },
    ],
    series: [
      {
        id: 'tank-level',
        name: 'Залишок',
        type: 'line',
        data: primaryData,
        smooth: 0.28,
        smoothMonotone: 'x',
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 0,
        connectNulls: false,
        lineStyle: { color: '#087781', width: 3 },
        emphasis: { disabled: true },
        areaStyle: { color: 'rgba(8, 119, 129, .055)' },
        z: 4,
      },
      ...deliverySegments,
      {
        id: 'delivery-points',
        name: 'Надходження',
        type: 'scatter',
        data: deliveryPoints,
        symbol: 'diamond',
        symbolSize: 15,
        itemStyle: { color: '#23a55a', borderColor: '#ffffff', borderWidth: 2 },
        emphasis: { scale: 1.35 },
        z: 9,
      },
    ],
  }
}

function tankLabel(tank = {}) {
  const product = tank.productName || 'невідомий продукт'
  const code = tank.productId == null ? '' : ` · код ${tank.productId}`
  return `Резервуар №${tank.tankNumber ?? '—'} · ${product}${code}`
}

function formatLiters(value) {
  return Number.isFinite(value) ? `${liters.format(value)} л` : '—'
}

function formatMoney(value, unit) {
  return Number.isFinite(value) ? `${money.format(value)} ${unit}` : '—'
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character])
}
